import { useState, useEffect } from 'react';

const DESKTOP_BREAKPOINT = 1024;

/**
 * Hook to detect if the current viewport is desktop-sized (>= 1024px)
 * @returns {boolean} true if viewport width is >= 1024px
 */
export function useIsDesktop() {
  // Initialize with false to avoid SSR hydration mismatch
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    // Check initial viewport size
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    };

    // Set initial value
    checkIsDesktop();

    // Debounced resize handler
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkIsDesktop, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return isDesktop;
}

/**
 * Hook to get viewport dimensions
 * @returns {{ width: number, height: number }}
 */
export function useViewportSize() {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };

    updateSize();

    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateSize, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return size;
}

export default useIsDesktop;

